import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { get, omit, partial, sortBy } from 'lodash';
import { mapPhotos, transformPhotosToImage } from '../../common/helpers/photos';
import Http from '../../http';
import * as types from './constants';
import HttpV2 from '../../httpv2';
import { startLoadingList, stopLoadingList } from '../../common/hoc/withLoaingList';
import { downloadCsv } from '../../main/reports/helpers';
import { fullName } from '../../common/helpers/users';

const ITEMS_PER_PAGE = 500;
const DEFAULT_INCLUDE = 'photos';

const fetchItems = async (url, skip = 0, HttpVx = Http) => {
  const response = await HttpVx.get(url, { include: DEFAULT_INCLUDE, limit: ITEMS_PER_PAGE, skip, sortBy: 'name' });
  return get(response, 'rows', []);
};

const dispatchFetchItems = (endpoint, HttpVx = Http) => async (dispatch) => {
  dispatch({ type: types.CLEAR_ITEMS });
  dispatch(showLoading());
  dispatch(startLoadingList());
  let skip = 0;
  let fetching = true;
  const items = [];
  while (fetching) {
    try {
      const newItems = await fetchItems(endpoint, skip, HttpVx); // eslint-disable-line
      items.push(...newItems);
      fetching = newItems.length >= ITEMS_PER_PAGE;
      skip += ITEMS_PER_PAGE;
    } catch (e) {
      fetching = false;
    }
  }

  dispatch({
    type: types.FETCH_ITEMS,
    payload: mapPhotos(sortBy(items, 'name')),
  });

  dispatch(stopLoadingList());
  dispatch(hideLoading());
};

export const fetchShopItemsV2 = shopId => dispatchFetchItems(`/shop/${shopId}/product`, HttpV2);
export const fetchBarberItemsV2 = (shopId, barberId) => dispatchFetchItems(
  `/shop/${shopId}/barber/${barberId}/product`,
  HttpV2,
);
export const fetchBrandItemsV2 = brandId => dispatchFetchItems(`/brand/${brandId}/product`, HttpV2);

export const fetchShopItems = shopId => dispatchFetchItems(`/shop/${shopId}/product`);
export const fetchBarberItems = barberId => dispatchFetchItems(`/barber/${barberId}/product`);

const exportProductsCsv = (url, name) => () => {
  const data = { view: 'csv' };
  return Http.getRaw(url, data, false, {
    'Squire-Version': '2019-12-25',
  })
    .then((response) => {
      downloadCsv(response, name);
    });
};

const exportProductsCsvV2 = (url, name) => () => {
  const data = { view: 'csv' };
  return HttpV2.getRaw(url, data, false)
    .then((response) => {
      downloadCsv(response, name);
    });
};

export const exportShopProductsCsv = shop =>
  exportProductsCsv(`/shop/${shop.id}/product`, `Products - ${shop.name}`);
export const exportBarberProductsCsv = barber =>
  exportProductsCsv(`/barber/${barber.id}/product`, `Products - ${fullName(barber)}`);
export const exportBrandProductsCsv = brand =>
  exportProductsCsvV2(`/brand/${brand.id}/product`, `Products - ${brand.name}`);
export const exportLowInventoryReportShopCsv = shop =>
  exportProductsCsv(`/shop/${shop.id}/low-inventory-report`, `Low Inventory Report - ${shop.name}`);
export const exportLowInventoryReportBarberCsv = barber =>
  exportProductsCsv(`/barber/${barber.id}/low-inventory-report`, `Low Inventory Report - ${fullName(barber)}`);

const fetchItem = (url, include = DEFAULT_INCLUDE) => dispatch =>
  HttpV2.get(url, { include })
    .then(response =>
      dispatch({
        type: types.FETCH_ITEM,
        payload: transformPhotosToImage(response),
      })
    );
export const fetchShopItem = (shopId, id) => fetchItem(`/shop/${shopId}/product/${id}`);
export const fetchBarberItem = (shopId, barberId, id) => fetchItem(`/shop/${shopId}/barber/${barberId}/product/${id}`);
export const fetchBrandItem = (brandId, id) => fetchItem(`/brand/${brandId}/product/${id}`);

const addBrandPhoto = (brandId, productId, image) =>
  HttpV2.postRaw(`/brand/${brandId}/product/${productId}/photo`, { image });
const addShopPhoto = (shopId, productId, image) =>
  HttpV2.postRaw(`/shop/${shopId}/product/${productId}/photo`, { image });
const addBarberPhoto = (shopId, barberId, productId, image) =>
  HttpV2.postRaw(`/shop/${shopId}/barber/${barberId}/product/${productId}/photo`, { image });

const updateBrandPhoto = (brandId, productId, photoId, image) =>
  HttpV2.putRaw(`/brand/${brandId}/product/${productId}/photo/${photoId}`, { image });
const updateShopPhoto = (shopId, productId, photoId, image) =>
  HttpV2.putRaw(`/shop/${shopId}/product/${productId}/photo/${photoId}`, { image });
const updateBarberPhoto = (shopId, barberId, productId, photoId, image) =>
  HttpV2.putRaw(`/shop/${shopId}/barber/${barberId}/product/${productId}/photo/${photoId}`, { image });

export const deleteBrandPhoto = (brandId, productId, photoId) =>
  HttpV2.delete(`/brand/${brandId}/product/${productId}/photo/${photoId}`);
export const deleteShopPhoto = (shopId, productId, photoId) =>
  HttpV2.delete(`/shop/${shopId}/product/${productId}/photo/${photoId}`);
export const deleteBarberPhoto = (shopId, barberId, productId, photoId) =>
  HttpV2.delete(`/shop/${shopId}/barber/${barberId}/product${productId}/photo/${photoId}`);

const addItemAction = payload => ({
  type: types.ADD_ITEM,
  payload,
});

const addItem = (url, data, addPhoto) => dispatch =>
  HttpV2.post(url, omit(data, 'image'))
    .then((response) => {
      if (data.image) {
        return addPhoto(response.id, data.image)
          .then(
            photoResponse => dispatch(addItemAction({
              ...response,
              image: photoResponse.url,
            }))
          );
      }
      return dispatch(addItemAction(response));
    });

export const addShopItem = (shopId, data) => addItem(
  `/shop/${shopId}/product`,
  data,
  partial(addShopPhoto, shopId)
);
export const addBarberItem = (shopId, barberId, data) => addItem(
  `/shop/${shopId}/barber/${barberId}/product`,
  data,
  partial(addBarberPhoto, shopId, barberId)
);
export const addBrandItem = (brandId, data) => addItem(
  `/brand/${brandId}/product`,
  data,
  partial(addBrandPhoto, brandId)
);

const updateItemAction = payload => ({
  type: types.UPDATE_ITEM,
  payload,
});

const updateItem = (url, data, id, addPhoto, deletePhoto) => dispatch =>
  HttpV2.put(url, omit(data, ['image', 'photos']))
    .then((response) => {
      if (data.image instanceof File) {
        const existentPhotoId = get(data, 'photos.0.id');
        const deletePromise = existentPhotoId ? deletePhoto(id, existentPhotoId) : Promise.resolve();
        return deletePromise.then(() => addPhoto(id, data.image))
          .then(photoResponse => dispatch(updateItemAction({
            ...response,
            image: photoResponse.url,
          })));
      }

      return dispatch(updateItemAction(response));
    });

export const updateShopItem = (shopId, id, data) =>
  updateItem(
    `/shop/${shopId}/product/${id}`,
    data,
    id,
    partial(addShopPhoto, shopId),
    partial(deleteShopPhoto, shopId)
  );
export const updateBarberItem = (shopId, barberId, id, data) =>
  updateItem(
    `/shop/${shopId}/barber/${barberId}/product/${id}`,
    data,
    id,
    partial(addBarberPhoto, shopId, barberId),
    partial(deleteBarberPhoto, shopId, barberId)
  );
export const updateBrandItem = (brandId, id, data) =>
  updateItem(
    `/brand/${brandId}/product/${id}`,
    data,
    id,
    partial(addBrandPhoto, brandId),
    partial(deleteBrandPhoto, brandId)
  );

const deleteItem = (url, id) => dispatch =>
  HttpV2.delete(url)
    .then(() =>
      dispatch({
        type: types.DELETE_ITEM,
        payload: id,
      })
    );
export const deleteShopItem = (shopId, id) =>
  deleteItem(`/shop/${shopId}/product/${id}`, id);
export const deleteBarberItem = (shopId, barberId, id) =>
  deleteItem(`/shop/${shopId}/barber/${barberId}/product/${id}`, id);
export const deleteBrandItem = (brandId, id) =>
  deleteItem(`/brand/${brandId}/product/${id}`, id);

export const updateProductInventory = (productId, shopId, data) => dispatch =>
  Http.post(`/product/${productId}/inventory`, { ...data, shopId })
    .then(res => (
      dispatch({
        type: types.UPDATE_INVENTORY,
        payload: res,
      })
    ));

const bulkUpdateInventory = (url, data) => dispatch => (
  Http.post(url, data)
    .then(res =>
      dispatch({
        type: types.BULK_UPDATE_INVENTORY,
        payload: res,
      })
    )
);

export const bulkUpdateShopInventory = (shopId, data) => (
  bulkUpdateInventory(`/shop/${shopId}/product/inventory`, data)
);

export const bulkUpdateBarberInventory = (barberId, data) => (
  bulkUpdateInventory(`/barber/${barberId}/product/inventory`, data)
);

export const bulkUpdateBrandInventory = (brandId, data) => (
  bulkUpdateInventory(`/brand/${brandId}/product/inventory`, data)
);
